export type DateFormat = 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd';
export type Currency = 'MXN' | 'USD';
export type DecimalSeparator = '.' | ',';
export type SpeedUnit = 'km/h' | 'mi/h';

export function formatDate(value: Date | string | undefined | null, dateFormat: DateFormat): string {
  if (!value) return '—';
  // "yyyy-MM-dd" (fecha sin hora) se interpreta como fecha local: `new Date('2026-10-05')`
  // es medianoche UTC y en México se mostraría un día antes.
  const dateOnly = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (dateFormat === 'dd/MM/yyyy') return `${day}/${month}/${year}`;
  if (dateFormat === 'MM/dd/yyyy') return `${month}/${day}/${year}`;
  return `${year}-${month}-${day}`;
}

export function formatCurrency(value: number, currency: Currency, decimalSeparator: DecimalSeparator): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const result = formatter.format(value);
  if (decimalSeparator === ',') {
    // en-US uses ',' for thousands and '.' for decimal — swap both in one pass
    return result.replace(/[,.]/g, (m) => (m === ',' ? '.' : ','));
  }
  return result;
}

export function formatSpeed(kmh: number, unit: SpeedUnit): string {
  if (unit === 'mi/h') {
    return `${(kmh * 0.621371).toFixed(1)} mi/h`;
  }
  return `${kmh.toFixed(1)} km/h`;
}

/** Texto guía del formato regional (placeholder de los campos de fecha). */
export function dateFormatHint(dateFormat: DateFormat): string {
  return dateFormat === 'yyyy-MM-dd' ? 'yyyy-MM-dd' : dateFormat;
}

/** Día y mes ("yyyy-MM-dd" → "30/10", "10/30" o "10-30") según el formato regional. Sin año, para ejes de gráficas. */
export function formatDayMonth(iso: string, dateFormat: DateFormat): string {
  const [, , mo, d] = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) ?? [];
  if (!d) return iso;
  if (dateFormat === 'MM/dd/yyyy') return `${mo}/${d}`;
  if (dateFormat === 'yyyy-MM-dd') return `${mo}-${d}`;
  return `${d}/${mo}`;
}

/** "yyyy-MM-dd" → texto en el formato regional ('' si no es una fecha ISO válida). */
export function isoToDisplayDate(iso: string | undefined | null, dateFormat: DateFormat): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  if (!m) return '';
  const [, y, mo, d] = m;
  if (dateFormat === 'dd/MM/yyyy') return `${d}/${mo}/${y}`;
  if (dateFormat === 'MM/dd/yyyy') return `${mo}/${d}/${y}`;
  return `${y}-${mo}-${d}`;
}

/**
 * Texto tecleado en el formato regional → "yyyy-MM-dd" (el ISO que espera el backend).
 * Acepta "/", "-" o "." como separador y días/meses de 1 dígito; null si no es una fecha real.
 */
export function parseDisplayDate(text: string, dateFormat: DateFormat): string | null {
  const parts = text.trim().split(/[\/.\-]/);
  if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) return null;
  let y: string; let mo: string; let d: string;
  if (dateFormat === 'dd/MM/yyyy') [d, mo, y] = parts as [string, string, string];
  else if (dateFormat === 'MM/dd/yyyy') [mo, d, y] = parts as [string, string, string];
  else [y, mo, d] = parts as [string, string, string];
  if (y.length !== 4) return null;
  const yn = Number(y); const mn = Number(mo); const dn = Number(d);
  const probe = new Date(yn, mn - 1, dn);
  if (probe.getFullYear() !== yn || probe.getMonth() !== mn - 1 || probe.getDate() !== dn) return null;
  return `${y}-${String(mn).padStart(2, '0')}-${String(dn).padStart(2, '0')}`;
}
